<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useFormat } from '../composables/useFormat'
import { cheapest, forPeriod, type ProviderQuote } from '../model/cost'
import { PRICES, pricesUpdatedAt } from '../model/priceBook'
import type { Spec } from '../model/spec'

/** The answer: one total per provider, all three periods, and which is cheapest. */
const props = defineProps<{
  /** `null` while a field is unusable. */
  quotes: readonly ProviderQuote[] | null
  discounts: Spec['discounts']
}>()

const { t } = useI18n()
const { money, percent, date } = useFormat()

const hours = PRICES.hoursPerMonth

// An incomplete total is lower than the real bill, so it can never be the cheapest.
const lowest = computed(() =>
  cheapest((props.quotes ?? []).map((quote) => (quote.complete ? quote.netMonthly : null))),
)
const lowestMonthly = computed(() => {
  const index = [...lowest.value][0]
  return index === undefined ? null : (props.quotes?.[index]?.netMonthly ?? null)
})
const highest = computed(() => Math.max(0, ...(props.quotes ?? []).map((q) => q.netMonthly)))

function share(quote: ProviderQuote): string {
  return highest.value === 0 ? '0%' : `${(quote.netMonthly / highest.value) * 100}%`
}

/** How much more than the cheapest, or null when there is nothing to compare with. */
function overCheapest(quote: ProviderQuote): number | null {
  const base = lowestMonthly.value
  if (base === null || base === 0 || !quote.complete) return null
  return quote.netMonthly / base - 1
}

const missing = (quote: ProviderQuote): number => quote.lines.filter((line) => !line).length
</script>

<template>
  <section class="panel totals" aria-labelledby="totals-heading">
    <h2 id="totals-heading" class="panel__title">{{ t('totals.heading') }}</h2>

    <ol v-if="quotes" class="totals__list" data-testid="totals">
      <li
        v-for="(quote, index) in quotes"
        :key="quote.provider"
        class="totals__item"
        :class="{ 'totals__item--cheapest': lowest.has(index) }"
        :data-provider="quote.provider"
      >
        <div class="totals__head">
          <span class="totals__name">{{ PRICES.providers[quote.provider].name }}</span>
          <span v-if="lowest.has(index)" class="totals__badge">{{ t('totals.cheapest') }}</span>
          <span v-if="discounts[quote.provider] > 0" class="totals__discount">
            <span aria-hidden="true">
              {{ t('totals.discount', { percent: percent(discounts[quote.provider] / 100) }) }}
            </span>
            <span class="visually-hidden">
              {{ t('totals.discountLabel', { percent: percent(discounts[quote.provider] / 100) }) }}
            </span>
          </span>
        </div>

        <p class="totals__amount">
          <span class="totals__value mono" data-testid="monthly">{{
            money(quote.netMonthly)
          }}</span>
          <span class="totals__unit">{{ ` ${t('totals.perMonth')}` }}</span>
        </p>

        <div class="totals__bar" aria-hidden="true">
          <span :style="{ width: share(quote) }" />
        </div>

        <p class="totals__periods mono">
          {{
            t('totals.periods', {
              hourly: money(forPeriod(quote.netMonthly, 'hour', hours), 'hour'),
              yearly: money(forPeriod(quote.netMonthly, 'year', hours), 'year'),
            })
          }}
        </p>

        <p v-if="!quote.complete" class="totals__note totals__note--warning">
          {{ t('totals.incomplete', missing(quote)) }}
        </p>
        <p v-else-if="overCheapest(quote) !== null && !lowest.has(index)" class="totals__note">
          {{ t('totals.versusCheapest', { percent: percent(overCheapest(quote) ?? 0, 0) }) }}
        </p>
      </li>
    </ol>

    <p v-else class="panel__empty">{{ t('totals.empty') }}</p>

    <p class="totals__updated">
      {{ t('totals.updated', { date: date(pricesUpdatedAt(PRICES)) }) }} ·
      <a href="#prices">{{ t('totals.aboutPrices') }}</a>
    </p>
  </section>
</template>

<style scoped>
.totals__list {
  display: flex;
  flex-direction: column;
  margin: var(--space-4) 0 0;
  padding: 0;
  list-style: none;
}

.totals__item {
  padding: var(--space-3) 0;
  border-top: 1px solid var(--color-border);
}

.totals__head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
}

.totals__name {
  font-weight: 600;
}

.totals__badge,
.totals__discount {
  padding: 0 var(--space-2);
  border: 1px solid currentColor;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.5;
}

.totals__badge {
  color: var(--color-success);
}

.totals__discount {
  color: var(--color-accent);
}

.totals__amount {
  margin: var(--space-1) 0 var(--space-2);
}

.totals__value {
  font-size: var(--text-xl);
  font-weight: 600;
}

.totals__item--cheapest .totals__value {
  color: var(--color-success);
}

.totals__unit {
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}

/* Lengths compare the four totals at a glance; the numbers carry the detail. */
.totals__bar {
  height: 0.375rem;
  border-radius: 999px;
  background: var(--color-surface-sunken);
  overflow: hidden;
}

.totals__bar span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: var(--color-accent);
  transition: width var(--transition-fast);
}

.totals__item--cheapest .totals__bar span {
  background: var(--color-success);
}

.totals__periods,
.totals__note {
  margin: var(--space-2) 0 0;
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}

.totals__note--warning {
  color: var(--color-warning);
}

.totals__updated {
  margin: var(--space-4) 0 0;
  padding-top: var(--space-3);
  border-top: 1px solid var(--color-border);
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}
</style>
